import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { moneyDisplay } from "@icraftx/shared/v1";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fetchTrialBalance, postJournal } from "@/shared/api/accounting";
import { fetchMe } from "@/shared/api/auth";
import { DocNumberField } from "@/modules/core/components/DocNumberField";
import { hasActionPermission } from "@/modules/shell/permission-client";
import { EmptyState } from "@/modules/shell/EmptyState";
import { PageFrame } from "@/modules/shell/PageFrame";
import { PageHeader } from "@/modules/shell/PageHeader";
import {
  queryErrorMessage,
  useAuthRedirectOnQueryError,
} from "@/shared/query/query-error";
import { queryKeys } from "@/shared/query/keys";

const OPENING_CASH_CODE = "1000";
const OPENING_EQUITY_CODE = "3000";

function todayYmd(): string {
  const n = new Date();
  const y = n.getFullYear();
  const m = String(n.getMonth() + 1).padStart(2, "0");
  const d = String(n.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function TrialBalancePage() {
  const { companySlug } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [canWriteGl, setCanWriteGl] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openDebit, setOpenDebit] = useState("");
  const [openCredit, setOpenCredit] = useState("");
  const [openMemo, setOpenMemo] = useState("Opening balances");
  const [openEntryDate, setOpenEntryDate] = useState(todayYmd);
  const [jvNumber, setJvNumber] = useState("");

  const tbQuery = useQuery({
    queryKey: queryKeys.gl.trialBalance(companySlug ?? ""),
    enabled: Boolean(companySlug),
    queryFn: () => fetchTrialBalance(companySlug!),
  });
  const errorMsg = queryErrorMessage(tbQuery.error, tbQuery.isError);
  useAuthRedirectOnQueryError(errorMsg);

  useEffect(() => {
    if (!companySlug) return;
    void fetchMe().then((me) => {
      const co = me.companies.find((c) => c.slug === companySlug);
      const held = me.user.isSuperadmin ? ["*"] : (co?.permissions ?? []);
      setCanWriteGl(hasActionPermission(held, "gl.write"));
    });
  }, [companySlug]);

  const opening = useMutation({
    mutationFn: async () => {
      return postJournal(companySlug!, {
        kind: "opening",
        memo: openMemo || "Opening balances",
        entryDate: openEntryDate,
        idempotencyKey: `jv-open-${crypto.randomUUID()}`,
        ...(jvNumber.trim() ? { number: jvNumber.trim() } : {}),
        lines: [
          { accountCode: OPENING_CASH_CODE, debit: openDebit || "0" },
          {
            accountCode: OPENING_EQUITY_CODE,
            credit: openCredit || openDebit || "0",
          },
        ],
      });
    },
    onSuccess: async ({ journal }) => {
      setOpenDebit("");
      setOpenCredit("");
      setOpenEntryDate(todayYmd());
      setJvNumber("");
      setError(null);
      if (journal?.id) {
        navigate(`/c/${companySlug}/gl/journals/${journal.id}`);
        return;
      }
      await queryClient.invalidateQueries({
        queryKey: queryKeys.gl.trialBalance(companySlug!),
      });
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Failed");
    },
  });

  if (!companySlug) return null;

  const tb = tbQuery.data ?? null;
  const displayError =
    error ??
    (errorMsg && errorMsg !== "Not signed in" ? errorMsg : null);

  return (
      <PageFrame width="narrow">
      <PageHeader
        title="Trial balance"
        description="From posted journals · openings default 0"
        actions={
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline" size="sm">
              <Link
                to={`/c/${companySlug}/gl/accounts`}
                className="no-underline"
              >
                Chart of accounts
              </Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link
                to={`/c/${companySlug}/gl/profit-loss`}
                className="no-underline"
              >
                Profit &amp; loss
              </Link>
            </Button>
          </div>
        }
      />

      {displayError && (
        <Alert variant="destructive" className="mt-4">
          {displayError}
        </Alert>
      )}

      {tbQuery.isPending || !tb ? (
        <Alert variant="muted" className="mt-8">
          Loading trial balance…
        </Alert>
      ) : (
        <>
          {tb.lines.length > 0 && !tb.balanced && (
            <Alert variant="destructive" className="mt-4">
              Out of balance — debit {moneyDisplay(tb.totalDebit)} vs credit{" "}
              {moneyDisplay(tb.totalCredit)}. Check recent posts.
            </Alert>
          )}
          {tb.lines.length === 0 ? (
            <div className="mt-8">
              <EmptyState
                title="No posted activity yet"
                description="Sell on POS, receive a PO, or post an opening JV if you have beginning cash."
                action={
                  <div className="flex flex-wrap justify-center gap-2">
                    <Button asChild variant="outline" size="sm">
                      <Link
                        to={`/c/${companySlug}/pos`}
                        className="no-underline"
                      >
                        Open POS
                      </Link>
                    </Button>
                    <Button asChild variant="outline" size="sm">
                      <Link
                        to={`/c/${companySlug}/purchasing/orders`}
                        className="no-underline"
                      >
                        Purchasing
                      </Link>
                    </Button>
                  </div>
                }
              />
            </div>
          ) : (
            <table className="mt-8 w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)] text-left text-xs uppercase tracking-wider text-[var(--color-muted)]">
                  <th className="py-2 pr-2 font-semibold">Account</th>
                  <th className="py-2 pr-2 text-right font-semibold">Debit</th>
                  <th className="py-2 text-right font-semibold">Credit</th>
                </tr>
              </thead>
              <tbody>
                {tb.lines.map((l) => (
                  <tr
                    key={l.accountId}
                    className="border-b border-[var(--color-border)]/60"
                  >
                    <td className="py-2 pr-2">
                      <Link
                        to={`/c/${companySlug}/gl/accounts/${l.accountId}`}
                        className="text-inherit no-underline hover:underline"
                      >
                        <span className="font-mono text-xs text-[var(--color-muted)]">
                          {l.code}
                        </span>{" "}
                        {l.name}
                      </Link>
                    </td>
                    <td className="py-2 pr-2 text-right font-mono">
                      {Number(l.debit) ? moneyDisplay(l.debit) : "—"}
                    </td>
                    <td className="py-2 text-right font-mono">
                      {Number(l.credit) ? moneyDisplay(l.credit) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="font-semibold">
                  <td className="py-3 pr-2">Total</td>
                  <td className="py-3 pr-2 text-right font-mono">
                    {moneyDisplay(tb.totalDebit)}
                  </td>
                  <td className="py-3 text-right font-mono">
                    {moneyDisplay(tb.totalCredit)}
                  </td>
                </tr>
                {tb.balanced && (
                  <tr>
                    <td
                      colSpan={3}
                      className="pt-1 text-xs font-normal text-[var(--color-ok)]"
                    >
                      Balanced
                    </td>
                  </tr>
                )}
              </tfoot>
            </table>
          )}

          {canWriteGl && (
            <form
              className="mt-10 space-y-3 border-t border-[var(--color-border)] pt-6"
              onSubmit={(e) => {
                e.preventDefault();
                opening.mutate();
              }}
            >
              <h2 className="m-0 text-sm font-semibold">
                Opening JV (optional)
              </h2>
              <p className="m-0 text-xs text-[var(--color-muted)]">
                If the shop has beginning cash, post once: debit Cash (
                {OPENING_CASH_CODE}) ↔ credit Equity ({OPENING_EQUITY_CODE}).
                Skip if starting at zero.
              </p>
              <Input
                value={openMemo}
                onChange={(e) => setOpenMemo(e.target.value)}
                placeholder="Memo"
              />
              <label className="block text-xs text-[var(--color-muted)]">
                Date of effect
                <Input
                  className="mt-1"
                  type="date"
                  required
                  value={openEntryDate}
                  onChange={(e) => setOpenEntryDate(e.target.value)}
                />
              </label>
              <DocNumberField
                companySlug={companySlug}
                docType="journal_entry"
                value={jvNumber}
                onChange={setJvNumber}
              />
              <div className="grid grid-cols-2 gap-2">
                <Input
                  className="font-mono"
                  placeholder="Debit cash"
                  value={openDebit}
                  onChange={(e) => setOpenDebit(e.target.value)}
                />
                <Input
                  className="font-mono"
                  placeholder="Credit equity (blank = same)"
                  value={openCredit}
                  onChange={(e) => setOpenCredit(e.target.value)}
                />
              </div>
              <Button
                type="submit"
                disabled={!openDebit}
                pending={opening.isPending}
                pendingLabel="Posting…"
              >
                Post opening
              </Button>
            </form>
          )}
        </>
      )}

      <p className="mt-8">
        <Button variant="ghost" asChild>
          <Link to={`/c/${companySlug}`} className="no-underline">
            ← Company
          </Link>
        </Button>
      </p>
    </PageFrame>
  );
}
